(() => {
  const STORAGE_KEY = 'mintside:wishlist';
  const WISHLIST_URL = '/pages/wishlist';

  const getWishlist = () => {
    try {
      const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      return Array.isArray(value) ? value.filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  };

  const setWishlist = (handles) => {
    const uniqueHandles = [...new Set(handles.filter(Boolean))];
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(uniqueHandles));
    updateCounts(uniqueHandles.length);
  };

  const addToWishlist = (handle) => {
    if (!handle) return;
    const current = getWishlist().filter((item) => item !== handle);
    setWishlist([handle, ...current]);
  };

  const removeFromWishlist = (handle) => {
    setWishlist(getWishlist().filter((item) => item !== handle));
  };

  const updateCounts = (count = getWishlist().length) => {
    document.querySelectorAll('[data-wishlist-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = element.hasAttribute('data-wishlist-count-hide-empty') && count === 0;
    });
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => {
    const replacements = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };

    return replacements[character];
  });

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'GBP';
    const locale = document.documentElement.lang || 'en-GB';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format((cents || 0) / 100);
    } catch (error) {
      return `£${((cents || 0) / 100).toFixed(2)}`;
    }
  };

  const getProduct = async (handle) => {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return null;

    const product = await response.json();
    return { ...product, handle: product.handle || handle };
  };

  const getProductPreorderStatus = async (handle) => {
    const response = await fetch(`/products/${encodeURIComponent(handle)}`, {
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) return false;

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const badge = documentFragment.querySelector('.product-page__media-badge');

    return Boolean(
      badge?.classList.contains('is-coming-soon') ||
      badge?.textContent?.trim().toLowerCase() === 'coming soon'
    );
  };

  const getWishlistProduct = async (handle) => {
    const product = await getProduct(handle);
    if (!product) return null;

    const isPreorder = await getProductPreorderStatus(product.handle || handle).catch(() => false);
    return { ...product, isPreorder };
  };

  const renderProductCard = (product) => {
    const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
    const image = product.featured_image || '';
    const productUrl = product.url || `/products/${product.handle}`;
    const canAdd = Boolean(variant?.id && variant.available && !product.isPreorder);
    const addLabel = product.isPreorder ? 'Coming soon' : canAdd ? 'Add to cart' : 'Sold out';

    return `
      <article class="wishlist-page__card" data-wishlist-card="${escapeHtml(product.handle)}">
        <a class="wishlist-page__image-link" href="${escapeHtml(productUrl)}">
          ${
            image
              ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">`
              : '<div class="wishlist-page__placeholder" aria-hidden="true"></div>'
          }
        </a>
        <div class="wishlist-page__card-body">
          <a class="wishlist-page__title" href="${escapeHtml(productUrl)}">${escapeHtml(product.title)}</a>
          <p class="wishlist-page__price">${formatMoney(product.price)}</p>
          <div class="wishlist-page__actions">
            <form class="wishlist-page__form" method="post" action="/cart/add">
              <input type="hidden" name="id" value="${escapeHtml(variant?.id || '')}">
              <button class="wishlist-page__add" type="submit" ${canAdd ? '' : 'disabled'}>
                <span>${addLabel}</span>
                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
                  <path d="M6.5 6h14l-1.8 8.3H8.1L6.5 6ZM6.5 6 6 3.5H3.5M9 19.5a.7.7 0 1 0 0-1.4.7.7 0 0 0 0 1.4ZM18 19.5a.7.7 0 1 0 0-1.4.7.7 0 0 0 0 1.4Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.7" />
                </svg>
              </button>
            </form>
            <button class="wishlist-page__remove" type="button" data-wishlist-remove data-product-handle="${escapeHtml(product.handle)}">
              Remove
            </button>
          </div>
        </div>
      </article>
    `;
  };

  const renderWishlistPage = async (root) => {
    const grid = root.querySelector('[data-wishlist-grid]');
    const empty = root.querySelector('[data-wishlist-empty]');
    const loading = root.querySelector('[data-wishlist-loading]');

    if (!grid || !empty || !loading) return;

    const handles = getWishlist();
    grid.innerHTML = '';
    empty.hidden = true;
    loading.hidden = handles.length === 0;

    if (handles.length === 0) {
      empty.hidden = false;
      return;
    }

    const products = await Promise.all(handles.map((handle) => getWishlistProduct(handle).catch(() => null)));
    const validProducts = products.filter(Boolean);
    const validHandles = validProducts.map((product) => product.handle).filter(Boolean);

    loading.hidden = true;
    setWishlist(handles.filter((handle) => validHandles.includes(handle)));

    if (validProducts.length === 0) {
      empty.hidden = false;
      return;
    }

    grid.innerHTML = validProducts.map(renderProductCard).join('');
  };

  const renderAllWishlistPages = () => {
    document.querySelectorAll('[data-wishlist-page]').forEach(renderWishlistPage);
  };

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-wishlist-add]');

    if (addButton) {
      event.preventDefault();
      addToWishlist(addButton.dataset.productHandle);
      if (addButton.hasAttribute('data-wishlist-stay')) {
        const label = addButton.lastChild;
        if (label && label.nodeType === Node.TEXT_NODE) label.textContent = ' Saved for later';
        addButton.setAttribute('aria-label', 'Saved for later');
        return;
      }
      window.location.href = addButton.getAttribute('href') || WISHLIST_URL;
      return;
    }

    const removeButton = event.target.closest('[data-wishlist-remove]');

    if (removeButton) {
      removeFromWishlist(removeButton.dataset.productHandle);
      const page = removeButton.closest('[data-wishlist-page]');
      if (page) renderWishlistPage(page);
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    updateCounts();
    renderAllWishlistPages();
  });

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-wishlist-page]').forEach(renderWishlistPage);
    updateCounts();
  });

  window.MintsideWishlist = {
    get: getWishlist,
    add: addToWishlist,
    remove: removeFromWishlist,
    render: renderAllWishlistPages,
  };
})();
